/**
 * @file `WorkspacePane` — the right-hand workspace surface: a tab strip
 *       (one tab per machine, with a live/idle dot + env label) above a
 *       single visible terminal pane.
 * @module @labcraft/lab/ui/components/workspace-pane
 *
 * Replaces the prior vertical `machines.map()` grid stack (design §4.3
 * — "machines are tabs, not a vertical stack"). Every machine's
 * {@link TerminalPane} stays **mounted** for the life of the chapter;
 * the non-selected ones are hidden via `display: none` (the `.hidden`
 * class) rather than unmounted, so each pane's xterm buffer + live
 * WebSocket survive tab switches. Selecting a tab just flips which pane
 * is visible — no remount, no reconnect.
 *
 * The live/idle dot reflects each pane's WS connection state. The
 * connection truth lives inside `TerminalPane` (it subscribes to the
 * `Terminal` status); we thread it up through `onStatusChange` and hold
 * a `machineId → status` map here so the tab dot tracks the real wire
 * state, not a static guess.
 */

import { useCallback, useState, type JSX, type ReactNode } from 'react'
import type { SubscriptionStatus } from '@labcraft/core/frontend'
import type { Machine } from '../../shared/routes.ts'
import type { Terminal } from '../../client/chapter.ts'
import { TerminalPane } from './terminal_pane.tsx'
import styles from './workspace_pane.module.css'

/**
 * One machine's render inputs for the workspace pane. A thin projection
 * of the wire {@link Machine} paired with its resolved client-side
 * {@link Terminal} and the per-pane callbacks the chapter page wires.
 *
 * @property machine - The wire machine record (see {@link Machine}).
 * @property terminal - The resolved frontend `Terminal` for this
 *   machine, or `null` while the pool hasn't primed it yet.
 * @property envLabel - Tab + header label (e.g. "Nix · shell").
 * @property onPause - Voluntary-suspend handler for this machine's
 *   Pause button.
 * @property onKeepAlive - Engagement-touch handler for the pre-expiry
 *   banner.
 * @property onExpiredRestart - Handler for the `terminating` overlay's
 *   "Start a fresh one" button.
 */
export type WorkspaceMachine = {
  machine: Machine
  terminal: Terminal | null
  envLabel: string
  onPause?: () => void
  onKeepAlive?: () => void
  onExpiredRestart?: () => void
}

/**
 * Props for {@link WorkspacePane}.
 *
 * @property machines - Every machine in the chapter recipe, in stable
 *   order. The first is selected by default.
 * @property footer - Optional surface docked at the bottom of the pane,
 *   below the terminal (the mentor dock, #939). Kept as a generic slot so
 *   the workspace pane stays mentor-agnostic.
 */
export type WorkspacePaneProps = {
  machines: WorkspaceMachine[]
  footer?: ReactNode
}

/**
 * Tab-strip + stacked-but-hidden terminal panes. See the file header
 * for the all-mounted survival mechanism.
 *
 * @param props - {@link WorkspacePaneProps}.
 * @returns The workspace pane element.
 */
export function WorkspacePane(props: WorkspacePaneProps): JSX.Element {
  const [selected, setSelected] = useState<string | undefined>(
    () => props.machines[0]?.machine.machineId,
  )
  const [statuses, setStatuses] = useState<Record<string, SubscriptionStatus>>(
    {},
  )

  const handleStatus = useCallback(
    (machineId: string, status: SubscriptionStatus): void => {
      setStatuses(prev =>
        prev[machineId] === status ? prev : { ...prev, [machineId]: status },
      )
    },
    [],
  )

  // The selected machine — fall back to the first when the prior
  // selection drops out of the recipe (e.g. a chapter switch shrank
  // the machine set).
  const activeId =
    props.machines.find(m => m.machine.machineId === selected)?.machine
      .machineId ?? props.machines[0]?.machine.machineId

  // Single machine: no tab strip — the lone terminal owns the pane.
  const showTabs = props.machines.length > 1

  return (
    <div className={styles.workspace}>
      {showTabs && (
        <div className={styles.tabs} role="tablist" aria-label="Machines">
          {props.machines.map(m => {
            const id = m.machine.machineId
            const live = statuses[id] === 'connected'
            const isActive = id === activeId
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={
                  isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab
                }
                onClick={() => setSelected(id)}>
                <span
                  className={`${styles.tabDot} ${live ? styles.tabDotLive : styles.tabDotIdle}`}
                  aria-hidden
                />
                <span className={styles.tabLabel}>{m.envLabel}</span>
              </button>
            )
          })}
        </div>
      )}
      <div className={styles.panes}>
        {props.machines.map(m => {
          const id = m.machine.machineId
          if (!m.terminal) return null
          const isActive = id === activeId
          return (
            <div
              key={id}
              className={
                isActive ? styles.pane : `${styles.pane} ${styles.hidden}`
              }
              role="tabpanel"
              aria-hidden={!isActive}>
              <TerminalPane
                terminal={m.terminal}
                envLabel={m.envLabel}
                onStatusChange={s => handleStatus(id, s)}
                onPause={m.onPause}
                onKeepAlive={m.onKeepAlive}
                onExpiredRestart={m.onExpiredRestart}
              />
            </div>
          )
        })}
      </div>
      {props.footer}
    </div>
  )
}
